import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { CalendarClock, Check, ChevronRight, PhoneCall, Users2, StickyNote, MessageCircle, BellRing, Cake } from "lucide-react";
import { toast } from "sonner";

const ICONS = { call: PhoneCall, meeting: Users2, followup: CalendarClock, note: StickyNote, whatsapp: MessageCircle, renewal: BellRing };

export default function Followups() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const [birthdays, setBirthdays] = useState([]);
  const load = () => {
    api.get("/followups").then((r) => setItems(r.data));
    api.get("/birthdays").then((r) => setBirthdays(r.data));
  };
  useEffect(() => { load(); }, []);

  const markDone = async (e, id) => {
    e.stopPropagation();
    await api.patch(`/activities/${id}/done`);
    toast.success("Marked as done");
    load();
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Follow-ups" subtitle={`${items.length} pending reminders in your scope`} />

      {birthdays.length > 0 && (
        <div className="card-surface rounded-xl p-5 mb-4" data-testid="birthdays-card">
          <div className="flex items-center gap-2 mb-3"><Cake className="h-4 w-4 text-gold" /><h3 className="font-display font-semibold">Upcoming Birthdays</h3></div>
          <div className="space-y-2">
            {birthdays.map((b) => (
              <div key={b.client_id} data-testid={`birthday-${b.client_id}`} className="flex items-center gap-3 px-3 py-2 rounded-lg row-hover hover:bg-secondary">
                <span className="text-lg">🎂</span>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{b.name}</div><div className="text-xs text-muted-foreground">Manager: {b.manager_name}</div></div>
                <span className="text-xs num text-gold font-medium">{b.days_until === 0 ? "Today" : b.days_until === 1 ? "Tomorrow" : `In ${b.days_until} days`} · {b.date_label}</span>
                <button onClick={() => navigate(`/clients/${b.client_id}`)} data-testid={`birthday-view-${b.client_id}`} className="text-xs font-medium text-primary hover:underline shrink-0">View Client</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="card-surface rounded-xl overflow-hidden" data-testid="followups-list">
        {items.length === 0 && <div className="py-16 text-center text-muted-foreground"><CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-40" />No pending follow-ups.</div>}
        {items.map((f) => {
          const Icon = ICONS[f.type] || CalendarClock;
          const overdue = f.follow_up_date && f.follow_up_date < today;
          return (
            <div key={f.id} onClick={() => navigate(`/clients/${f.client_id}`)} data-testid={`followup-${f.id}`}
              className="flex items-center gap-4 px-5 py-3.5 cursor-pointer row-hover hover:bg-secondary border-b border-border/40 last:border-0">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${overdue ? "bg-destructive/15 text-destructive" : "bg-secondary text-primary"}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {f.client_name}
                  {f.renewal && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gold/15 text-gold font-semibold">RENEWAL</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{f.note}</div>
              </div>
              <div className="text-right hidden sm:block">
                <div className={`text-xs num font-medium ${overdue ? "text-destructive" : "text-gold"}`}>
                  {new Date(f.follow_up_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  {overdue && " · Overdue"}
                </div>
                <div className="text-[10px] text-muted-foreground">{f.created_by_name}</div>
              </div>
              <button onClick={(e) => markDone(e, f.id)} data-testid={`followup-done-${f.id}`}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 row-hover">
                <Check className="h-4 w-4" />
              </button>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
